import type { FlowConfig, FlowFieldConfig } from './types'

/**
 * Convert a raw string value from the API into the type expected by
 * React Hook Form / Zod for a given field type.
 */
function coerceFieldValue(field: FlowFieldConfig, raw: string): unknown {
  switch (field.type) {
    case 'checkbox':
      return raw === 'true' || raw === '1'

    case 'hidden':
      // Hidden fields with defaultValue false MAY store booleans (e.g. bando_1 = "true"/"false")
      // or arbitrary strings (e.g. tipologia_1 = "Collegio", durata = "1").
      // Only coerce to boolean when the raw value is the literal string "true" or "false".
      if (field.defaultValue === false) {
        if (raw === 'true') return true
        if (raw === 'false') return false
      }
      return raw

    case 'number':
      if (raw === '') return undefined
      return Number(raw)

    case 'date-picker-airbnb':
      // Keep as ISO string — buildDefaultValues converts it to Date when loading
      return raw || ''

    default:
      return raw
  }
}

interface StoreActions {
  setDraftId: (id: string) => void
  resetStore: () => void
  updateStepData: (stepKey: string, data: Record<string, unknown>) => void
  markStepCompleted: (step: number) => void
  setCurrentStep: (step: number) => void
}

function getLegacyNationalityValue(flatValues: Record<string, string>): string | undefined {
  return flatValues['nazionalita'] ?? flatValues['nazionalità']
}

/**
 * Backward compatibility for old drafts where birth country was stored as
 * `nazionalita` with values like `Italiana|1` or `Estera|0`.
 */
function normalizeLegacyBirthCountry(flatValues: Record<string, string>): void {
  // Support both old field name and new field name
  if (flatValues['nazione_residenza'] || flatValues['nazione_nascita']) {
    // Migrate old field name to new
    if (flatValues['nazione_nascita'] && !flatValues['nazione_residenza']) {
      flatValues['nazione_residenza'] = flatValues['nazione_nascita']
    }
    return
  }

  const legacyNationality = getLegacyNationalityValue(flatValues)
  if (!legacyNationality) return

  const normalized = legacyNationality.trim().toLowerCase()

  // If legacy value clearly indicates Italian nationality, map to ITA.
  if (normalized === 'italiana|1' || normalized.endsWith('|1')) {
    flatValues['nazione_residenza'] = 'ITA'
  }
}

/**
 * Restore a loaded draft into the wizard store.
 *
 * The API groups saved data by `saveStep` (1-based), while the store
 * groups data by flow step id (`step1`, `step2`, …). This function
 * flattens the API values, distributes them to the correct flow steps,
 * and coerces types so that form fields accept them.
 *
 * @returns The slug of the next step the user should land on.
 */
export function restoreDraftToStore(
  draftId: string,
  apiSteps: { values: { name: string; value: string }[] }[],
  flow: FlowConfig,
  actions: StoreActions
): string {
  // 0. Clear any existing form state before restoring the draft
  actions.resetStore()

  // 1. Flatten all API values into a single map
  const flatValues: Record<string, string> = {}
  for (const step of apiSteps) {
    for (const v of step.values ?? []) {
      flatValues[v.name] = v.value
    }
  }

  normalizeLegacyBirthCountry(flatValues)

  const legacyNationality = getLegacyNationalityValue(flatValues)
  const hasMissingBirthCountryFromLegacy =
    !!legacyNationality &&
    !flatValues['nazione_residenza'] &&
    legacyNationality.toLowerCase().endsWith('|0')

  // 2. Determine the last saved saveStep (1-based)
  const lastSaveStep = apiSteps.length

  // 3. For each flow step whose saveStep <= lastSaveStep,
  //    extract matching fields and populate the store.
  for (const step of flow.steps) {
    const hasExplicitSaveStep = step.saveStep !== undefined
    const stepSaveStep = step.saveStep ?? step.id

    // Steps with no explicit saveStep are "pre-save" collection steps.
    // Their data is always included in the first API payload, so restore
    // them unconditionally. Only guarded steps (explicit saveStep) should
    // stop the loop when past the last saved point.
    if (hasExplicitSaveStep && stepSaveStep > lastSaveStep) break

    const stepFormData: Record<string, unknown> = {}
    let hasData = false

    for (const field of step.fields) {
      if (field.key in flatValues) {
        stepFormData[field.key] = coerceFieldValue(field, flatValues[field.key])
        hasData = true
      }
    }

    // richiestaListaAttesa is not a declared field (it maps to the API's
    // lista_attesa flag), so it is restored onto the housing selection step
    // here to avoid being silently dropped and defaulted back to false.
    if (flatValues['lista_attesa'] === 'true' && step.fields.some((f) => f.key === 'sede_camplus_1')) {
      stepFormData['richiestaListaAttesa'] = true
      hasData = true
    }

    if (hasData) {
      actions.updateStepData(`step${step.id}`, stepFormData)
      actions.markStepCompleted(step.id)
    } else if (hasExplicitSaveStep) {
      // A guarded step with no matching fields still counts as completed
      // (e.g. a save boundary step that had all its data in flatValues but
      // none of its declared fields match – rare, but keep symmetry)
      actions.markStepCompleted(step.id)
    }
  }

  // 4. Set the draft ID
  actions.setDraftId(draftId)

  // 4b. Legacy drafts with `nazionalita=Estera|0` must re-enter at the step
  //     containing `nazione_residenza` so the user can select the residence country.
  if (hasMissingBirthCountryFromLegacy) {
    const birthCountryStep = flow.steps.find((s) =>
      s.fields.some((f) => f.key === 'nazione_residenza')
    )
    if (birthCountryStep) {
      actions.setCurrentStep(birthCountryStep.id)
      return birthCountryStep.slug
    }
  }

  // 5. Find the first step whose saveStep > lastSaveStep (the next step to fill).
  //    Only consider steps with an explicit saveStep so that pre-save steps
  //    (id 1-3, no saveStep) are never used as the re-entry point.
  //    The minimum re-entry step is the first with saveStep defined (id:4, saveStep:1).
  const nextStep = flow.steps.find((s) => s.saveStep !== undefined && s.saveStep > lastSaveStep)
  if (nextStep) {
    actions.setCurrentStep(nextStep.id)
    return nextStep.slug
  }

  // If all steps are saved, go to the last step (summary)
  const lastStep = flow.steps[flow.steps.length - 1]
  actions.setCurrentStep(lastStep.id)
  return lastStep.slug
}
